import { GetSession } from "./session.helper";
import { history } from "./history.helper";
import language from '../data/language.json';
import countries from '../data/countries.json';
import { ENABLE_LOCAL_LANGUAGE_ONLY } from "../config";
import { setCountry, setLanguage } from '../redux/actions';
import moment from 'moment';

export const fetchLanguage = ({ country }) => {
    if (GetSession("currentPage") !== null && country?.code) {
        const countryLanguage = (countries?.find(item => item?.code === country?.code))?.languages;
        // const result = countryLanguage?.map(item => {
        //     if (languageFlag) {
        //         if ((item !== 'en' && country?.code !== 'GB') || (item === 'en' && country?.code === 'GB')) {
        //             return language?.find(lang => lang?.code?.toLowerCase() === item?.toLowerCase())
        //         }
        //     } else {
        //         return language?.find(lang => lang?.code?.toLowerCase() === item?.toLowerCase())
        //     }
        // });
        const resultArr = [];
        countryLanguage?.forEach(item => {
            if (ENABLE_LOCAL_LANGUAGE_ONLY) {
                if ((item !== 'en' && country?.code !== 'GB') || (item === 'en' && country?.code === 'GB')) {
                    resultArr.push(language?.find(lang => lang?.code?.toLowerCase() === item?.toLowerCase()))
                }
            } else {
                resultArr.push(language?.find(lang => lang?.code?.toLowerCase() === item?.toLowerCase()))
            }
        });
        return resultArr;
    } else {
        return language;
    }
}

/**
 * Normalizes an incoming language code ("en" or "en-ES") and returns the lowercase language segment.
 * Returns null when the value is empty/invalid.
 */
export const normalizeLanguageCode = (languageCode) => {
    if (!languageCode) return null;
    const langLower = String(languageCode).split('-')[0]?.toLowerCase();
    return langLower || null;
};

/**
 * Resolves the country that should be paired with the given language code.
 * Prefers `currentCountry` when it supports the language, otherwise falls back
 * to the first country in countries.json that lists the language.
 */
export const resolveCountryForLanguage = (langLower, currentCountry) => {
    if (!langLower) return null;
    if (currentCountry?.languages?.includes(langLower)) {
        return currentCountry;
    }
    return countries?.find(c => c?.languages?.includes(langLower)) || currentCountry || null;
};

/**
 * Rewrites the language segment of the current URL (e.g. /es-ES/... -> /en-ES/...)
 * so refresh/deep-links stay in sync with the active language.
 */
export const replaceUrlLanguage = (langLower, countryCode) => {
    if (!langLower || !countryCode) return;
    const currentPath = history?.location?.pathname || (typeof window !== 'undefined' ? window?.location?.pathname : '') || '/';
    const search = history?.location?.search || (typeof window !== 'undefined' ? window?.location?.search : '') || '';
    const hash = history?.location?.hash || (typeof window !== 'undefined' ? window?.location?.hash : '') || '';
    const segments = currentPath.split('/');
    if (segments[1] && segments[1].includes('-')) {
        segments[1] = `${langLower}-${countryCode}`;
    } else {
        segments.splice(1, 0, `${langLower}-${countryCode}`);
    }
    const nextPath = segments.join('/');
    if (nextPath !== currentPath) {
        history?.replace(`${nextPath}${search}${hash}`);
    }
};

/**
 * Applies a language change end-to-end:
 *   1. Normalize the incoming language code.
 *   2. Resolve the country that should be paired with it (unless one is forced).
 *   3. Update i18n + moment locales.
 *   4. Dispatch redux updates for language and country.
 *   5. Rewrite the language segment in the URL (no-op when the URL already matches).
 *
 * A meeting place can support multiple languages (see meetingPlace.json ->
 * countryCode and countries.json -> languages), so callers only need to pass
 * the newly selected language code — the rest is derived here.
 *
 * @param {object} params
 * @param {string} params.languageCode - New language code ("en" or "en-ES").
 * @param {string} params.currentLanguage - Currently active language code (redux state).
 * @param {object} params.currentCountry - Currently active country object (redux state).
 * @param {object} params.i18n - i18next instance (from useTranslation()).
 * @param {Function} params.dispatch - Redux dispatch function.
 * @param {object} [params.targetCountry] - Optional country override. Use this when the
 *   caller already knows the target country (e.g. URL-driven sync where the URL's
 *   country segment must win over the fallback lookup).
 * @returns {boolean} true if the language was applied, false otherwise (no-op or invalid input).
 */
export const applyLanguageChange = ({ languageCode, currentLanguage, currentCountry, i18n, dispatch, targetCountry: forcedCountry }) => {
    const langLower = normalizeLanguageCode(languageCode);
    if (!langLower) return false;

    const targetCountry = forcedCountry?.code
        ? forcedCountry
        : resolveCountryForLanguage(langLower, currentCountry);
    if (!targetCountry?.code) return false;

    const languageAlreadyActive = currentLanguage?.toLowerCase() === langLower;
    const countryAlreadyActive = currentCountry?.code === targetCountry.code;
    if (languageAlreadyActive && countryAlreadyActive) return false;

    if (!languageAlreadyActive) {
        i18n?.changeLanguage?.(`${langLower}-${targetCountry.code}`);
        moment.locale(langLower);
        dispatch?.(setLanguage(langLower));
    }
    if (!countryAlreadyActive) {
        dispatch?.(setCountry(targetCountry));
    }

    replaceUrlLanguage(langLower, targetCountry.code);
    return true;
};